// Module: VideoVerdictScreen
// Role: Template rendered for video moderation verdict (4 branches).
//       Matches pantene-flow.jsx:1004 PF_VideoVerdict + VerdictBlock.
// Uses: flow/atoms (Frame, CheckRow), styles/tokens (P, S, T),
//       components/atoms/WizardCtaBar
// Used by: FlowScreen (rejected + escalated verdict dispatch for video checkpoints)
// Glossary: ai/glossary/flow_engine.md

import { useNavigate } from 'react-router-dom';
import { Frame } from '../atoms/Frame';
import { CheckRow } from '../atoms/CheckRow';
import { WizardCtaBar } from '../../../components/atoms/WizardCtaBar';
import { P, S, T } from '../../../styles/tokens';
import type { Checkpoint, CheckpointAttempt, SlotFlowRun } from '../../../api/flow-types';

// ─── Color constants matching prototype VerdictBlock ───────────────────────────

const ACCEPTED_BG = '#EFF7E9';
const ESCALATED_BG = '#FFF6E5';
const SOFT_FAIL_BG = '#FFEFE5';
const HARD_FAIL_BG = '#FDE8E8';

// ─── Props ────────────────────────────────────────────────────────────────────

interface VideoVerdictProps {
  checkpoint: Checkpoint;
  attempt: CheckpointAttempt;
  flowRun: SlotFlowRun;
  checkpoints: Checkpoint[];
  /** Called when buyer taps "К публикациям" (accepted). */
  onCta: () => void;
  /** Called when buyer taps "Переснять видео" (soft_fail retry). */
  onRetry: () => void;
}

// ─── VerdictBlock — reusable card ─────────────────────────────────────────────

interface VerdictBlockProps {
  color: string;
  bg: string;
  title: string;
  text: string;
  reasons?: string[];
}

function VerdictBlock({ color, bg, title, text, reasons }: VerdictBlockProps) {
  return (
    <div
      style={{
        marginTop: 18,
        background: bg,
        borderRadius: 18,
        padding: 18,
        border: `1px solid ${color}33`,
      }}
    >
      <div style={{ font: T.title3, color, fontWeight: 700 }}>{title}</div>
      <div
        style={{
          font: T.footnote,
          color: S.label,
          marginTop: 8,
          lineHeight: 1.55,
        }}
      >
        {text}
      </div>
      {reasons && reasons.length > 0 && (
        <div
          style={{
            marginTop: 12,
            background: '#fff',
            borderRadius: 12,
            padding: 12,
          }}
        >
          <div
            style={{
              font: T.caption,
              color: S.secondaryLabel,
              fontWeight: 700,
              textTransform: 'uppercase',
              marginBottom: 6,
            }}
          >
            Причины
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {reasons.map((r, i) => (
              <CheckRow key={i} text={r} status="fail" />
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function VideoVerdictScreen({
  checkpoint,
  attempt,
  onCta,
  onRetry,
}: VideoVerdictProps) {
  const navigate = useNavigate();
  const verdict = attempt.verdict;
  const remaining = checkpoint.max_attempts - attempt.attempt_number;
  const isHardFail = verdict === 'rejected' && remaining <= 0;
  const isSoftFail = verdict === 'rejected' && remaining > 0;

  return (
    <Frame>
      <div
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '8px 16px 16px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* ── Accepted ── */}
        {verdict === 'accepted' && (
          <VerdictBlock
            color={P.success}
            bg={ACCEPTED_BG}
            title="Видео одобрено!"
            text="Отлично! Видео прошло проверку. Переходи к публикациям."
          />
        )}

        {/* ── Escalated to moderation ── */}
        {verdict === 'escalated_to_moderation' && (
          <VerdictBlock
            color={P.warn}
            bg={ESCALATED_BG}
            title="Ручная проверка"
            text="ИИ не уверен — модератор посмотрит сам. Обычно — до 2 часов в рабочее время. Никаких действий не нужно, мы пришлём push."
          />
        )}

        {/* ── Soft fail (can retry) ── */}
        {isSoftFail && (
          <VerdictBlock
            color={P.warn}
            bg={SOFT_FAIL_BG}
            title="Видео не прошло"
            text={`Можешь переснять и загрузить ещё раз. Осталось попыток: ${remaining} из ${checkpoint.max_attempts}.`}
            reasons={attempt.reject_reasons}
          />
        )}

        {/* ── Hard fail (no retries left) ── */}
        {isHardFail && (
          <VerdictBlock
            color={P.error}
            bg={HARD_FAIL_BG}
            title="Видео отклонено"
            text="К сожалению, нельзя переснять. Слот закрыт, кешбэк не начислится. Если считаешь, что это ошибка — напиши оператору."
            reasons={attempt.reject_reasons}
          />
        )}
      </div>

      {/* ── CTA bar ── */}
      {verdict === 'accepted' && (
        <WizardCtaBar label="К публикациям" onClick={onCta} />
      )}
      {isSoftFail && (
        <WizardCtaBar label="Переснять видео" onClick={onRetry} />
      )}
      {isHardFail && (
        <WizardCtaBar
          label="Написать оператору"
          kind="ink"
          onClick={() => navigate('/help')}
        />
      )}
      {/* escalated_to_moderation: NO CTA — buyer waits for push (EC-1) */}
    </Frame>
  );
}
